import PropTypes from "prop-types";
import React, {
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
    LoadingContext,
    M_StateActions,
    MTable,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the User CRUD page in a tab.
 *
 * Important:
 * - kept as "UserConfig" to preserve the existing routing behavior.
 */
const USER_CRUD_ROUTE = "UserConfig";

//endregion

//region Helpers

/**
 * Normalizes role permissions received from the parent.
 *
 * Purpose:
 * - supports role objects with Value;
 * - supports plain string permissions;
 * - avoids passing undefined entries to MTable.
 *
 * @param {Array<object|string>|undefined} roles - Incoming roles.
 * @returns {Array<string>|undefined} Role permission values.
 */
const getRolePermissions = (roles) => {
    if (!Array.isArray(roles)) return undefined;

    return roles
        .map((role) => role?.Value ?? role)
        .filter(Boolean);
};

/**
 * Builds a readable label value for a User row.
 *
 * Purpose:
 * - keeps tab labels safe when FirstName is missing;
 * - supports alternative fields without breaking the current payload.
 *
 * @param {object|null|undefined} row - Selected user row.
 * @returns {string} User display name.
 */
const getUserDisplayName = (row) =>
    row?.FirstName ?? row?.Name ?? row?.UserName ?? row?.Email ?? "";

//endregion

//region Component

/**
 * Renders the Users configuration page.
 *
 * Responsibilities:
 * - render the common tabs configuration-page layout;
 * - keep the page visually aligned with other tab Config pages through config-page.css;
 * - render the Users MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - keep User CRUD state isolated through M_State.
 *
 * Important:
 * - this page is rendered inside a tabs section;
 * - therefore it uses config-page--tabs and the __*-tabs element modifiers;
 * - Copy is disabled to keep user creation explicit.
 *
 * @param {object} props - Component props.
 * @param {Array<object|string>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Users configuration page.
 */
export default function UserConfig({
    roles,
    onCreateTab,
    toastMessages,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Keeps table permissions stable and resilient to different role payload shapes.
     */
    const rolePermissions = useMemo(
        () => getRolePermissions(roles),
        [roles]
    );

    /**
     * Removes table actions that are not required in this configuration page.
     *
     * Important:
     * - Print is not part of the desired Users workflow;
     * - Copy is disabled to keep user creation explicit.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy"],
        []
    );

    /**
     * Opens the User CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected user row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected user row.
     * @returns {void}
     */
    const openUserCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "UserLine",
                        value: row,
                    })
                );
            }

            const userName = getUserDisplayName(row);

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewUser")
                    : operation === "View"
                        ? `${t("LabelViewUser")} "${userName}"`
                        : `${t("LabelEditUser")} "${userName}"`;

            onCreateTab(USER_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based User workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openUserCrud("View", row)],
                ["Create", () => openUserCrud("Create")],
                ["Edit", (row) => openUserCrud("Edit", row)],
            ]),
        [openUserCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header config-page__header--tabs">
                <div className="config-page__title config-page__title--tabs">
                    {t("Users")}
                </div>

                <div className="config-page__description config-page__description--tabs">
                    {t("InfoUsers")}
                </div>
            </div>

            <div className="config-page__table config-page__table--tabs">
                <MTable
                    filtersOnTable
                    entityName="Users"
                    roleName="Users"
                    rolePermissions={rolePermissions}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                    tabsRailWidth={250}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

UserConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion